/**
 * Whether a lead that is already linked to `contact` still needs saving as one of its alternate
 * contacts (REM-3497). Covers leads linked before alternates existed: the lead is a different
 * person, or brings an email/mobile the contact doesn't hold, and no alternate entry carries its
 * `lead_id` yet. Mirrors the duplicate dialog's rules so both screens agree.
 */
export function leadNeedsAlternateEntry(lead: any, contact: any): boolean {
  if (!lead?._id || !contact) {
    return false;
  }
  const alternates: any[] = contact.alternate_contacts ?? [];
  if (alternates.some(item => String(item?.lead_id ?? '') === String(lead._id))) {
    return false;
  }

  const leadName = normalizeText(lead.full_name || `${lead.first_name ?? ''} ${lead.last_name ?? ''}`);
  const contactName = normalizeText(contact.full_name || `${contact.first_name ?? ''} ${contact.last_name ?? ''}`);
  const isOtherPerson = !!leadName && leadName !== contactName;

  const email = normalizeText(lead.email);
  const knownEmails = [contact.email, ...(contact.secondary_emails ?? []).map(item => item?.email), ...alternates.map(item => item?.email)];
  const isNewEmail = !!email && !knownEmails.some(value => normalizeText(value) === email);

  const mobile = normalizePhone(lead.mobile_phone);
  const knownMobiles = [
    contact.mobile_no,
    ...(contact.secondary_mobile ?? []).map(item => item?.mobile_no),
    ...alternates.map(item => item?.mobile_no),
  ];
  const isNewMobile = !!mobile && !knownMobiles.some(value => normalizePhone(value) === mobile);

  return isOtherPerson || isNewEmail || isNewMobile;
}

function normalizeText(value: string): string {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** Same rule as the backend's normalizePhone (convert-to-contact): '+61 4…' and '04…' compare equal. */
function normalizePhone(value: string): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  if (digits.length > 9 && digits.startsWith('61')) {
    return `0${digits.slice(2)}`;
  }
  if (digits.startsWith('0')) {
    return digits;
  }
  return digits.length === 9 ? `0${digits}` : digits;
}
